import { useCallback } from 'react';
import { logError } from 'utils/logger';
import { useNotifications } from 'modules/notification';
import { useLazyQuery, useMutation } from '@apollo/client';
import { QuoteProposalViewModal } from '@trnsact/trnsact-shared-types';
import { FetchOptionsCallback, GraphqlResolverInput, MutationResponse } from 'global';
import {
  UPDATE_PROPOSAL,
  CREATE_PROPOSAL,
  CreateQuoteDto,
  CreateProposalDTO,
  GET_PROPOSAL_SNAPSHOT,
  CREATE_QUOTE_PROPOSAL,
  CreateQuotePreviewDto,
  ProposalSnapshotResponse,
  ProposalSnapshotVariables,
  CREATE_QUOTE_PROPOSAL_PREVIEW,
  CREATE_PROPOSAL_PREVIEW,
  CreateProposalPreviewResponse,
  CREATE_PROPOSAL_ON_SUBMISSION_APPROVAL,
} from '../api';

export function useProposal() {
  const { showNotification } = useNotifications();

  const [getSnapshot] = useLazyQuery<ProposalSnapshotResponse, ProposalSnapshotVariables>(GET_PROPOSAL_SNAPSHOT);

  const [createProposal, { loading: isProposalCreating }] = useMutation(CREATE_PROPOSAL);
  const [createProposalOnSubmissionApproval, { loading: isProposalCreatingOnSubmissionApproval }] = useMutation(
    CREATE_PROPOSAL_ON_SUBMISSION_APPROVAL
  );

  const handleCreateProposal = useCallback(
    async (dto: CreateProposalDTO) => {
      try {
        await createProposal({ variables: { input: dto } });

        showNotification('Proposal successfully created!');
      } catch (error) {
        logError(error);
        showNotification('Something went wrong', { type: 'error' });
      }
    },
    [showNotification, createProposal]
  );

  const handleCreateProposalOnSubmissionApproval = useCallback(
    async (dto: CreateProposalDTO, notification?: { recipient: string; internalEmails: string[] }) => {
      try {
        await createProposalOnSubmissionApproval({
          variables: { input: { proposal: dto, ...(notification ? { notification } : {}) } },
        });

        showNotification('Proposal successfully created!');
      } catch (error) {
        logError(error);
        showNotification('Something went wrong', { type: 'error' });
      }
    },
    [createProposalOnSubmissionApproval, showNotification]
  );

  const [updateProposal, { loading: isProposalUpdating }] = useMutation(UPDATE_PROPOSAL, {
    context: { authRequired: true },
    refetchQueries: ['Proposal'],
  });

  const handleUpdateProposal = useCallback(
    async (id: string, fieldsObj: Record<string, any>) => {
      try {
        await updateProposal({ variables: { id, fieldsObj } });

        showNotification('Status updated!');
      } catch (error) {
        logError(error);
        showNotification('Status not updated!', { type: 'error' });
      }
    },
    [showNotification, updateProposal]
  );

  const handleGetProposalSnapshot = useCallback(
    async (proposalId: string) => {
      const { data } = await getSnapshot({ variables: { id: proposalId } });
      return data?.proposal?.snapshot ?? null;
    },
    [getSnapshot]
  );

  const [createQuoteProposal, { loading: isQuoteProposalCreating }] = useMutation<
    MutationResponse<QuoteProposalViewModal>,
    GraphqlResolverInput<CreateQuoteDto>
  >(CREATE_QUOTE_PROPOSAL);

  const handleCreateQuoteProposal = useCallback(
    async (dto: CreateQuoteDto, fetchOptions?: FetchOptionsCallback<{}>) => {
      try {
        const { data } = await createQuoteProposal({
          variables: { input: dto },
          ...(typeof fetchOptions == 'function' ? fetchOptions({}) : {}),
        });

        showNotification('Proposal successfully created!');

        if (!data?.result?.entity) return null;

        return data.result.entity.id;
      } catch (error) {
        logError(error);
        showNotification('Something went wrong!', { type: 'error' });

        return null;
      }
    },
    [showNotification, createQuoteProposal]
  );

  const [createQuoteProposalPreview, { loading: isQuoteProposalPreviewCreating }] = useMutation<
    MutationResponse<QuoteProposalViewModal>,
    GraphqlResolverInput<CreateQuotePreviewDto>
  >(CREATE_QUOTE_PROPOSAL_PREVIEW);

  const handleCreatePreviewQuoteProposal = useCallback(
    async (dto: CreateQuotePreviewDto, fetchOptions?: FetchOptionsCallback<{}>) => {
      try {
        const { data } = await createQuoteProposalPreview({
          variables: {
            input: {
              voId: dto.voId,
              notesToBuyer: dto.notesToBuyer,
              proposalSettings: dto.proposalSettings,
            },
          },
          ...(typeof fetchOptions == 'function' ? fetchOptions({}) : {}),
        });

        showNotification('Proposal preview successfully created!');

        if (!data?.result?.entity) return null;

        return data.result.entity.id;
      } catch (error) {
        logError(error);
        showNotification('Something went wrong!', { type: 'error' });

        return null;
      }
    },
    [showNotification, createQuoteProposalPreview]
  );

  const [createProposalPreview, { loading: isProposalPreviewCreating }] = useMutation<
    CreateProposalPreviewResponse,
    { input: CreateProposalDTO }
  >(CREATE_PROPOSAL_PREVIEW);

  const handleCreateProposalPreview = useCallback(
    async (dto: CreateProposalDTO) => {
      try {
        const { data } = await createProposalPreview({ variables: { input: dto } });

        if (!data?.createProposalPreview?.id) return null;

        return data.createProposalPreview.id;
      } catch (error) {
        logError(error);
        showNotification('Something went wrong!', { type: 'error' });

        return null;
      }
    },
    [showNotification, createProposalPreview]
  );

  return {
    isProposalUpdating,
    handleUpdateProposal,
    handleCreateProposal,
    handleGetProposalSnapshot,
    handleCreateProposalPreview,
    handleCreateProposalOnSubmissionApproval,
    isProposalPreviewCreating,
    isProposalCreating: isProposalCreating || isProposalCreatingOnSubmissionApproval,

    isQuoteProposalCreating,
    handleCreateQuoteProposal,
    isQuoteProposalPreviewCreating,
    handleCreatePreviewQuoteProposal,
  };
}
